import React, { useEffect, useState } from "react";
import { motion } from "framer-motion";
import { ArrowRightIcon, CalendarIcon, CheckCircle2Icon, ClockIcon, MailIcon, MessageSquareIcon, PhoneIcon, UserIcon, BoxIcon } from "lucide-react";
import { COMPANY } from "../data/company";
interface ContactStep {
  icon: BoxIcon;
  title: string;
  description: string;
}
const contactSteps: ContactStep[] = [{
  icon: ClockIcon,
  title: 'Revisamos tu mensaje',
  description: 'Leemos tu contexto y detectamos los puntos que conviene abordar primero.'
}, {
  icon: MessageSquareIcon,
  title: 'Definimos la conversación',
  description: 'Te respondemos para entender prioridades, materiales y alcance.'
}, {
  icon: CalendarIcon,
  title: 'Aterrizamos el siguiente paso',
  description: 'Si hay encaje, acordamos la forma más clara de avanzar.'
}];
const sectors = [{
  value: 'hosteleria',
  label: 'Hostelería'
}, {
  value: 'servicios',
  label: 'Servicios locales'
}, {
  value: 'comercio',
  label: 'Comercio'
}, {
  value: 'profesional',
  label: 'Marca personal o profesional'
}, {
  value: 'otro',
  label: 'Otro'
}];
/** Campos homogéneos: misma altura, mismo borde, mismo estado de foco. */
const FIELD_CLASS = 'h-[52px] w-full border border-white/20 bg-black px-4 text-sm text-white transition-colors placeholder:text-neutral-500 hover:border-white/35 focus:border-white focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a]';
const LABEL_CLASS = 'mb-2 block text-[10px] font-semibold uppercase tracking-[0.18em] text-neutral-300';
export function ContactPage() {
  const [formStatus, setFormStatus] = useState<'idle' | 'submitting' | 'success'>('idle');
  const [formData, setFormData] = useState({
    name: '',
    business: '',
    email: '',
    phone: '',
    service: '',
    message: ''
  });
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);
  const handleSubmit = (event: React.FormEvent) => {
    event.preventDefault();
    setFormStatus('submitting');
    window.setTimeout(() => {
      setFormStatus('success');
      window.setTimeout(() => setFormStatus('idle'), 4000);
    }, 900);
  };
  const updateField = (field: keyof typeof formData, value: string) => setFormData(current => ({
    ...current,
    [field]: value
  }));
  return <div className="min-h-screen bg-[#0a0a0a] pt-20 sm:pt-24" data-id="element-258" data-mp="31vyomhlbvlp1">
      <section className="page-header" data-id="element-259" data-mp="3pkhu6pcztkdf">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-260" data-mp="3pljbhbklr33l">
          <motion.div initial={{
          opacity: 0,
          y: 12
        }} animate={{
          opacity: 1,
          y: 0
        }} transition={{
          duration: 0.28,
          ease: [0.23, 1, 0.32, 1]
        }} className="grid gap-7 lg:grid-cols-[0.8fr_1.2fr] lg:items-end" data-id="element-261" data-mp="3ujb8gk03tfen">
            <div data-id="element-262" data-mp="ce82yy6871dp">
              <p className="eyebrow" data-id="element-263" data-mp="cdpw77lxt4rj">Contacto</p>
              <h1 className="mt-5 text-4xl font-medium tracking-[-0.055em] text-white sm:text-5xl lg:text-6xl" data-id="element-264" data-mp="cbv9pov0p4lc">
                Cuéntanos qué necesita tu negocio ahora.
              </h1>
            </div>
            <p className="max-w-2xl text-base leading-relaxed text-neutral-300 sm:text-lg" data-id="element-265" data-mp="21t0oxl3hqfp3">
              Completa el formulario y utilizaremos la información para entender
              el punto de partida de tu proyecto. Sin mensajes automatizados ni
              compromisos innecesarios.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-shell" data-id="element-266" data-mp="1ixakzsbzz98k">
        <div className="container mx-auto max-w-6xl px-6 md:px-12" data-id="element-267" data-mp="1iy2emxm1aww7">
          <div className="grid overflow-hidden border border-white/10 lg:grid-cols-[0.85fr_1.15fr]" data-id="element-268" data-mp="1dypghgppq3eb">
            <aside className="flex flex-col bg-black p-7 sm:p-10 lg:border-r lg:border-white/10" data-id="element-269" data-mp="1sw01bgo8wlmp">
              <p className="eyebrow" data-id="element-270" data-mp="1ntr7yj7gnhk2">Después de escribirnos</p>
              <h2 className="mt-5 text-2xl font-medium tracking-[-0.035em] text-white" data-id="element-271" data-mp="348sr4lxh7004">
                Una primera conversación con contexto.
              </h2>
              <div className="mt-9 space-y-7" data-id="element-272" data-mp="2zctvcd9tifpo">
                {contactSteps.map(step => {
                const StepIcon = step.icon;
                return <div key={step.title} className="flex gap-4" data-id="element-273" data-mp="1drowfx79m56t">
                      <span className="flex h-9 w-9 shrink-0 items-center justify-center border border-white/20 text-white" data-id="element-274" data-mp="1hu20mh6d5v1h">
                        <StepIcon size={16} aria-hidden="true" data-id="element-275" data-mp="1mtew1qbawafo" />
                      </span>
                      <div data-id="element-276" data-mp="1rsrjac633jis">
                        <h3 className="text-sm font-medium text-white" data-id="element-277" data-mp="1rrgfwdgjwds7">
                          {step.title}
                        </h3>
                        <p className="mt-2 text-sm leading-relaxed text-neutral-300" data-id="element-278" data-mp="1woztdb5fcmj3">
                          {step.description}
                        </p>
                      </div>
                    </div>;
              })}
              </div>
              <div className="mt-10 border-t border-white/10 pt-6 lg:mt-auto" data-id="element-279" data-mp="2ajsgaunn94zp">
                <p className="meta-label" data-id="element-280" data-mp="2fhasctwyk0bx">Soporte directo</p>
                <ul className="mt-4 space-y-3" data-id="element-281" data-mp="2ffpy75ek2jel">
                  <li data-id="element-282" data-mp="2pm0up0w9x6go">
                    <a href={`mailto:${COMPANY.supportEmail}`} className="inline-flex items-center gap-3 text-sm text-white underline underline-offset-4 transition-colors hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-283" data-mp="2pnc0155dam9w">
                      <MailIcon size={15} className="shrink-0" aria-hidden="true" data-id="element-284" data-mp="2bmhnqbf7815y" />
                      {COMPANY.supportEmail}
                    </a>
                  </li>
                  <li data-id="element-285" data-mp="38e3905lg0anr">
                    <a href={COMPANY.whatsappUrl} target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-3 text-sm text-white underline underline-offset-4 transition-colors hover:text-neutral-300 focus:outline-none focus-visible:ring-2 focus-visible:ring-white" data-id="element-286" data-mp="33cnl85rvrq1o">
                      <PhoneIcon size={15} className="shrink-0" aria-hidden="true" data-id="element-287" data-mp="3r2hxom8afcle" />
                      {COMPANY.phone}
                    </a>
                  </li>
                </ul>
                <p className="mt-4 text-xs leading-relaxed text-neutral-300" data-id="element-288" data-mp="1kehi4pc8wdyx">
                  Atendemos consultas antes y después de la compra. Respondemos en un plazo máximo de 3 días hábiles.
                </p>
                <p className="mt-5 flex items-center gap-2 text-xs font-medium text-neutral-200" data-id="element-289" data-mp="1ap7qfalbcxbc">
                  <CheckCircle2Icon size={15} className="shrink-0 text-white" aria-hidden="true" data-id="element-290" data-mp="1fn98ptax0slr" />
                  Tus datos se tratan de forma confidencial.
                </p>
              </div>
            </aside>

            <motion.form initial={{
            opacity: 0,
            y: 12
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.28,
            delay: 0.06,
            ease: [0.23, 1, 0.32, 1]
          }} onSubmit={handleSubmit} className="bg-[#0a0a0a] p-7 sm:p-10" data-id="element-291" data-mp="16o4bcqb3b5b6">
              <div className="grid gap-5 md:grid-cols-2" data-id="element-292" data-mp="2m4mq4m4wq3a5">
                <label className="block" data-id="element-293" data-mp="2r367e98k2qw6">
                  <span className={LABEL_CLASS} data-id="element-294" data-mp="2r0tuiahnn6pz">Nombre completo *</span>
                  <span className="relative block" data-id="element-295" data-mp="fgcld3vn91j1">
                    <UserIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-296" data-mp="aghpsn0xbnrn" />
                    <input required value={formData.name} onChange={event => updateField('name', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="Tu nombre" autoComplete="name" data-id="element-297" data-mp="p6yse9paojq4" />
                  </span>
                </label>
                <label className="block" data-id="element-298" data-mp="u4h1c03bayy4">
                  <span className={LABEL_CLASS} data-id="element-299" data-mp="u3p6wslgb79f">Negocio *</span>
                  <input required value={formData.business} onChange={event => updateField('business', event.target.value)} className={FIELD_CLASS} placeholder="Nombre de tu negocio" autoComplete="organization" data-id="element-300" data-mp="183zzc02estwp" />
                </label>
                <label className="block" data-id="element-301" data-mp="39weggwx4puc2">
                  <span className={LABEL_CLASS} data-id="element-302" data-mp="39xg03ps7np5e">Correo electrónico *</span>
                  <span className="relative block" data-id="element-303" data-mp="34ycv25c8dixf">
                    <MailIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-304" data-mp="2j3w2lit7dx12" />
                    <input required type="email" value={formData.email} onChange={event => updateField('email', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="tu@email.com" autoComplete="email" data-id="element-305" data-mp="2o0ct2jnvknhb" />
                  </span>
                </label>
                <label className="block" data-id="element-306" data-mp="205jum0e12t72">
                  <span className={LABEL_CLASS} data-id="element-307" data-mp="206uys1bdy2yp">Teléfono *</span>
                  <span className="relative block" data-id="element-308" data-mp="254cyr27fd7uk">
                    <PhoneIcon size={16} className="pointer-events-none absolute left-4 top-1/2 -translate-y-1/2 text-neutral-400" aria-hidden="true" data-id="element-309" data-mp="2a2xzd2ql9tjj" />
                    <input required type="tel" value={formData.phone} onChange={event => updateField('phone', event.target.value)} className={`${FIELD_CLASS} pl-11`} placeholder="+34 600 000 000" autoComplete="tel" data-id="element-310" data-mp="2f3c9qrfnxfkq" />
                  </span>
                </label>
              </div>
              <label className="mt-5 block" data-id="element-311" data-mp="1esjawwjyv6l8">
                <span className={LABEL_CLASS} data-id="element-312" data-mp="1evdteijf6hc0">Sector o actividad *</span>
                <select required value={formData.service} onChange={event => updateField('service', event.target.value)} className={`${FIELD_CLASS} appearance-none bg-[url("data:image/svg+xml;charset=utf-8,%3Csvg xmlns='http://www.w3.org/2000/svg' viewBox='0 0 24 24' fill='none' stroke='%23a3a3a3' stroke-width='2'%3E%3Cpath d='m6 9 6 6 6-6'/%3E%3C/svg%3E")] bg-[length:16px] bg-[right_1rem_center] bg-no-repeat pr-11 ${formData.service ? 'text-white' : 'text-neutral-500'}`} data-id="element-313" data-mp="19uzubpzsqhq4">
                  <option value="" disabled data-id="element-314" data-mp="m5yn262p9cmf">
                    Selecciona una opción
                  </option>
                  {sectors.map(sector => <option key={sector.value} value={sector.value} data-id="element-315" data-mp="h7x3zl59xday">
                      {sector.label}
                    </option>)}
                </select>
              </label>
              <label className="mt-5 block" data-id="element-316" data-mp="2bkd4047h7v7d">
                <span className={LABEL_CLASS} data-id="element-317" data-mp="xf55l9ngvlua">
                  Sobre el proyecto{' '}
                  <span className="normal-case tracking-normal text-neutral-400" data-id="element-318" data-mp="sgam05hodt94">
                    (opcional)
                  </span>
                </span>
                <textarea rows={5} value={formData.message} onChange={event => updateField('message', event.target.value)} className={`${FIELD_CLASS} h-auto resize-none py-3.5 leading-relaxed`} placeholder="Qué quieres mejorar, qué necesitas ordenar o qué objetivo tienes en mente." data-id="element-319" data-mp="16fjsgdg2x8zw" />
              </label>
              <button type="submit" disabled={formStatus !== 'idle'} className="mt-7 flex h-[52px] w-full items-center justify-center gap-2 bg-white px-5 text-sm font-semibold text-black transition-colors hover:bg-neutral-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-white focus-visible:ring-offset-2 focus-visible:ring-offset-[#0a0a0a] disabled:cursor-not-allowed disabled:opacity-70" data-id="element-320" data-mp="9rnkty2mdo0t">
                {formStatus === 'idle' && <>
                    Enviar consulta{' '}
                    <ArrowRightIcon size={16} aria-hidden="true" data-id="element-321" data-mp="3cz7fcdylf3fn" />
                  </>}
                {formStatus === 'submitting' && 'Enviando...'}
                {formStatus === 'success' && 'Consulta recibida'}
              </button>
              <p className="mt-4 text-center text-xs leading-relaxed text-neutral-400" data-id="element-322" data-mp="381p8ct44yyaa">
                Al enviar, aceptas que utilicemos esta información para
                responder a tu consulta.
              </p>
            </motion.form>
          </div>
        </div>
      </section>
    </div>;
}